import React from "react";
import { Clock, FolderOpen, Search, X } from "lucide-react";
import { useEtatGlobal } from "../EtatGlobal.js";
import { useClients } from "../Portefeuille/useClients.js";
import { GedTab } from "./GedTab";
import { gedCorrespond, gedNorm, gedSetContexte, gedSignature } from "./gedModel";
import "./GedClient.css";

// Page GED : on choisit un client, puis sa GED (onglet GED de la fiche client
// de la maquette Claude Design, ged.jsx) s'affiche en pleine page, avec ses
// vrais documents (table DocumentGED, partagée avec MyALFA Link).
const CLE_CLIENT = "myalfa_ged_client";
const CLE_RECENTS = "myalfa_ged_recents";
const RECENTS_MAX = 8;

const lire = (cle, defaut) => {
  try {
    const v = JSON.parse(localStorage.getItem(cle));
    return v === null || v === undefined ? defaut : v;
  } catch (e) {
    return defaut;
  }
};
const ecrire = (cle, v) => {
  try {
    localStorage.setItem(cle, JSON.stringify(v));
  } catch (e) {
    // stockage indisponible : rien n'est retenu
  }
};

function GedClient() {
  const { utilisateur } = useEtatGlobal();
  const { data: clients = [] } = useClients();
  const [clientId, setClientId] = React.useState(() => lire(CLE_CLIENT, null));
  const [recents, setRecents] = React.useState(() => lire(CLE_RECENTS, []));
  const [q, setQ] = React.useState("");
  const [ouvert, setOuvert] = React.useState(false);
  const [actif, setActif] = React.useState(0); // ligne surlignée au clavier
  const champ = React.useRef(null);
  const boite = React.useRef(null);

  gedSetContexte(gedSignature(utilisateur), utilisateur?.userCabinets?.[0]?.cabinet?.name);

  React.useEffect(() => ecrire(CLE_CLIENT, clientId), [clientId]);
  React.useEffect(() => ecrire(CLE_RECENTS, recents), [recents]);

  const choisir = (id) => {
    setClientId(id);
    setRecents((r) => [id, ...r.filter((x) => x !== id)].slice(0, RECENTS_MAX));
    setQ("");
    setOuvert(false);
    if (champ.current) champ.current.blur();
  };

  React.useEffect(() => {
    const fermer = (e) => {
      if (boite.current && !boite.current.contains(e.target)) setOuvert(false);
    };
    document.addEventListener("mousedown", fermer);
    return () => document.removeEventListener("mousedown", fermer);
  }, []);

  // Recherche instantanée sur la liste des clients déjà en cache, souple :
  // sans accents ni casse, ponctuation ignorée, mots dans n'importe quel ordre,
  // sur le nom du dossier et le nom du contact. Les noms qui commencent par la
  // saisie passent en premier ; 25 résultats au maximum.
  const suggestions = React.useMemo(() => {
    const debut = gedNorm(q);
    if (!debut) return [];
    return clients
      .filter((c) => gedCorrespond(`${c.nom || ""} ${c.prenomClient || ""} ${c.nomClient || ""}`, q))
      .sort((a, b) => Number(gedNorm(b.nom).startsWith(debut)) - Number(gedNorm(a.nom).startsWith(debut)) || (a.nom || "").localeCompare(b.nom || "", "fr"))
      .slice(0, 25);
  }, [q, clients]);
  const clientsRecents = recents.map((id) => clients.find((c) => c.id === id)).filter(Boolean);

  // Liste affichée sous le champ : résultats de la saisie, ou récents si vide.
  const liste = q.trim() ? suggestions : clientsRecents;
  React.useEffect(() => setActif(0), [q]);
  const auClavier = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOuvert(true);
      setActif((i) => Math.min(i + 1, Math.max(liste.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActif((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && liste[actif]) {
      e.preventDefault();
      choisir(liste[actif].id);
    } else if (e.key === "Escape") {
      setOuvert(false);
      e.currentTarget.blur();
    }
  };
  const contactDe = (c) => [`${c.prenomClient || ""} ${c.nomClient || ""}`.trim(), c.email].filter(Boolean).join(" · ");
  const initiales = (nom) =>
    gedNorm(nom)
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((m) => m[0].toUpperCase())
      .join("") || "?";

  const fiche = clients.find((c) => c.id === clientId);
  const client = fiche
    ? { id: fiche.id, name: fiche.nom || "Client", email: fiche.email, nomClient: fiche.nomClient, prenomClient: fiche.prenomClient }
    : null;

  const ligne = (c, i) => (
    <li key={c.id}>
      <button
        type="button"
        onMouseEnter={() => setActif(i)}
        onClick={() => choisir(c.id)}
        className={`w-full flex items-center gap-3 text-left px-3 py-2 rounded-lg ${i === actif ? "bg-[#f5ede8]" : "hover:bg-slate-50"}`}
      >
        <span className="w-8 h-8 rounded-lg bg-[#f5ede8] text-[#840040] text-[11px] font-semibold flex items-center justify-center flex-shrink-0">
          {initiales(c.nom)}
        </span>
        <span className="min-w-0 flex-1">
          <span className={`block truncate text-sm ${c.id === clientId ? "font-semibold text-[#840040]" : "text-gray-800"}`}>{c.nom}</span>
          {contactDe(c) && <span className="block truncate text-[11px] text-gray-400">{contactDe(c)}</span>}
        </span>
      </button>
    </li>
  );

  return (
    <div className="relative">
      <div className="mb-4">
        <h1 className="text-xl font-semibold text-gray-900 m-0">GED</h1>
        <p className="text-xs text-gray-400 mt-1">Espace documentaire partagé avec le client (MyALFA Link)</p>
      </div>

      {/* Sélection du client : bandeau toujours visible */}
      <div className="mb-4 bg-white border border-slate-200 rounded-xl shadow-sm p-4">
        <div className="flex items-center gap-4 flex-wrap">
          {client ? (
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <span className="w-11 h-11 rounded-xl bg-[#840040] text-white text-sm font-semibold flex items-center justify-center flex-shrink-0">
                {initiales(client.name)}
              </span>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wide text-gray-400 m-0">Client</p>
                <p className="text-base font-semibold text-gray-900 m-0 truncate">{client.name}</p>
                {contactDe(fiche) && <p className="text-xs text-gray-500 m-0 truncate">{contactDe(fiche)}</p>}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <span className="w-11 h-11 rounded-xl bg-[#f5ede8] text-[#840040] flex items-center justify-center flex-shrink-0">
                <FolderOpen className="w-5 h-5" />
              </span>
              <div>
                <p className="text-base font-semibold text-gray-900 m-0">Choisissez un client</p>
                <p className="text-xs text-gray-500 m-0">Sa GED s'affiche ici : documents du cabinet et documents déposés par le client.</p>
              </div>
            </div>
          )}

          <div className="relative w-full sm:w-[440px]" ref={boite}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              ref={champ}
              type="text"
              autoFocus={!client}
              value={q}
              placeholder={client ? "Changer de client : nom, contact…" : "Rechercher un client : nom, contact…"}
              onChange={(e) => {
                setQ(e.target.value);
                setOuvert(true);
              }}
              onFocus={() => setOuvert(true)}
              onKeyDown={auClavier}
              className="pl-9 pr-9 h-11 w-full rounded-lg bg-white border border-gray-300 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#840040] focus:ring-2 focus:ring-[#840040]/15"
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ("")}
                aria-label="Effacer la recherche"
                className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-md text-gray-400 hover:text-gray-600 hover:bg-slate-100"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            {ouvert && (q.trim() || clientsRecents.length > 0) && (
              <div className="absolute z-30 mt-1.5 w-full bg-white border border-gray-200 rounded-xl shadow-xl p-1.5">
                {!q.trim() && <p className="px-3 pt-1.5 pb-1 m-0 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Clients récents</p>}
                {q.trim() && suggestions.length === 0 ? (
                  <p className="px-3 py-4 m-0 text-sm text-gray-400 text-center">Aucun client ne correspond à « {q.trim()} »</p>
                ) : (
                  <ul className="m-0 p-0 list-none max-h-80 overflow-y-auto">{liste.map(ligne)}</ul>
                )}
                {liste.length > 0 && (
                  <p className="px-3 pt-1.5 pb-0.5 m-0 text-[10px] text-gray-400 border-t border-slate-100 mt-1">
                    ↑ ↓ pour naviguer · Entrée pour ouvrir · Échap pour fermer
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {clientsRecents.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap mt-3 pt-3 border-t border-slate-100">
            <span className="inline-flex items-center gap-1 text-[11px] text-gray-400">
              <Clock className="w-3.5 h-3.5" /> Récents
            </span>
            {clientsRecents.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => choisir(c.id)}
                className={`px-2.5 py-1 rounded-full border text-[11px] transition-colors ${
                  c.id === clientId
                    ? "bg-[#840040] border-[#840040] text-white"
                    : "bg-white border-gray-200 text-gray-700 hover:border-[#840040] hover:text-[#840040]"
                }`}
              >
                {c.nom}
              </button>
            ))}
          </div>
        )}
      </div>

      {client ? (
        <div className="gedx ged-panneau">
          <GedTab key={client.id} client={client} />
        </div>
      ) : (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl py-16 text-center">
          <FolderOpen className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500 m-0">Recherchez un client ci-dessus pour afficher sa GED.</p>
        </div>
      )}
    </div>
  );
}

export default GedClient;
